import { Box, CircularProgress, Typography } from "@mui/material"
import { SearchOutlined } from "@mui-symbols-material/w300"
import { useState } from "react"
import type { FC, UIEvent } from "react"
import { useFormContext } from "react-hook-form"

import { FilterCheckbox } from "@/components/common/Filters"
import { InputField } from "@/components/common/InputField"
import type { WarehouseAreaListFilter } from "@/graphql/codegen/graphql"
import { useGetBaseCompanies } from "@/hooks/useGetBaseCompanies"
import { CompanyType } from "@/types/companyType"
import useDebounce from "@/utils/useDebounce"

interface WarehouseCompaniesFilterProps {
  onFilterChange: (name: keyof WarehouseAreaListFilter, value: any) => void
}

export const WarehouseCompaniesFilter: FC<WarehouseCompaniesFilterProps> = ({ onFilterChange }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const { control, watch } = useFormContext()
  const companiesValue = watch("companyIds") ?? []
  const debouncedSearchString = useDebounce(searchTerm, 300)

  const { companies, isFetchingCompanies, loadMoreCompanies } = useGetBaseCompanies({
    query: debouncedSearchString,
    types: [CompanyType.AkrochemWarehouse, CompanyType.ConsignmentWarehouse],
  })

  const handleFilterChange = (newValue: string[]) => {
    onFilterChange("companyIds", newValue)
  }

  const canLoadMore = (element: UIEvent) => {
    const el = element.currentTarget
    return !isFetchingCompanies && el.scrollTop + el.clientHeight >= el.scrollHeight
  }

  return (
    <Box display='flex' flexDirection='column' height='100%' data-test='warehouseCompaniesFilter'>
      <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
        Company
      </Typography>

      <Box className='flex-none p-4'>
        <InputField
          placeholder='Search by Company Name'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          startAdornment={<SearchOutlined className='text-gray-600' />}
          endAdornment={isFetchingCompanies && <CircularProgress size={20} />}
          data-testid='warehouse-companies-search-input'
        />
      </Box>

      <Box className='flex h-full grow flex-col overflow-hidden'>
        <Box
          className='grow overflow-y-auto overflow-x-hidden px-8 py-2'
          onScroll={(event) => {
            if (canLoadMore(event)) {
              loadMoreCompanies()
            }
          }}
        >
          {isFetchingCompanies && (
            <Box display='flex' justifyContent='center' py={2}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!isFetchingCompanies && companies.length === 0 && (
            <Box display='flex' justifyContent='center' py={2}>
              <Typography variant='body2' color='textSecondary'>
                No companies found
              </Typography>
            </Box>
          )}

          {companies.map((company) => (
            <FilterCheckbox
              key={company.companyId}
              label={company.name ?? ""}
              value={company.companyId}
              control={control}
              name='roleIds'
              values={companiesValue}
              onFilterChange={handleFilterChange}
            />
          ))}
        </Box>
      </Box>
    </Box>
  )
}
